import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import PharmatechLayout from "../components/PharmatechLayout";
import { useNavigate } from "react-router-dom";
import {
  Stethoscope, MessageSquare, Users, Search, Activity,
  Shield, TrendingUp, AlertTriangle, Brain, History, Award, ScanLine
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const MODULES = [
  { path: "/pharmatech/copilot", icon: Stethoscope, title: "Copilot du Pharmacien", desc: "Analysez les ordonnances, détectez les interactions médicamenteuses et évaluez les risques en temps réel.", color: "#0ea5e9" },
  { path: "/pharmatech/chat", icon: MessageSquare, title: "PharmaGPT", desc: "Assistant conversationnel IA spécialisé dans la pharmacologie africaine. Posez vos questions cliniques.", color: "#06b6d4" },
  { path: "/pharmatech/profil-patient", icon: Users, title: "Profil Patient Virtuel", desc: "Simulez l'ajout de médicaments au profil existant d'un patient et évaluez les risques cumulés.", color: "#10b981" },
  { path: "/pharmatech/recherche", icon: Search, title: "Google du Médicament", desc: "Recherche avancée de médicaments, génériques, alternatives thérapeutiques avec fiches complètes.", color: "#8b5cf6" },
  { path: "/pharmatech/pharmacovigilance", icon: AlertTriangle, title: "Pharmacovigilance", desc: "Signalez les effets indésirables, contribuez à la sécurité des patients.", color: "#f59e0b" },
  { path: "/pharmatech/ordonnances", icon: ScanLine, title: "Scanner d'Ordonnances", desc: "Scannez et validez les ordonnances classiques et bons d'assurance avec l'IA.", color: "#6366f1" },
  { path: "/pharmatech/score", icon: Award, title: "Score Qualité", desc: "Évaluez la performance de votre officine et améliorez vos pratiques.", color: "#ec4899" },
];

export default function PharmatechDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pharmatech/stats`, { headers: getHeaders() }).then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <PharmatechLayout title="CMES Pharmatech" subtitle="Intelligence Pharmaceutique Africaine">
      <div data-testid="pharmatech-dashboard" className="space-y-8 max-w-5xl mx-auto">
        {/* Disclaimer */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <Shield className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
          <p className="text-xs leading-relaxed text-amber-800">
            <strong>Avertissement juridique :</strong> CMES Pharmatech est un outil d'aide à la décision.
            Les informations sont fournies à titre d'orientation et ne se substituent en aucun cas à l'expertise
            du pharmacien, seul responsable des conseils qu'il dispense. En cas de doute, consultez les références
            officielles (RCP, Vidal) ou le prescripteur.
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard icon={Activity} label="Analyses" value={stats.total_analyses} color="#0ea5e9" />
            <StatCard icon={Stethoscope} label="Copilot" value={stats.copilot_count} color="#3b82f6" />
            <StatCard icon={Users} label="Simulateur" value={stats.digital_twin_count} color="#10b981" />
            <StatCard icon={MessageSquare} label="PharmaGPT" value={stats.chat_messages} color="#06b6d4" />
            <StatCard icon={AlertTriangle} label="Vigilance" value={stats.vigilance_reports || 0} color="#f59e0b" />
          </div>
        )}

        {/* Risk distribution */}
        {stats && stats.total_analyses > 0 && (
          <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" /> Distribution des risques
            </h3>
            <div className="flex gap-4">
              <RiskPill label="Vert" count={stats.risk_distribution.vert} color="#22c55e" />
              <RiskPill label="Orange" count={stats.risk_distribution.orange} color="#f59e0b" />
              <RiskPill label="Rouge" count={stats.risk_distribution.rouge} color="#ef4444" />
            </div>
          </div>
        )}

        {/* Modules */}
        <div className="grid md:grid-cols-3 gap-4">
          {MODULES.map(m => (
            <button key={m.path} onClick={() => navigate(m.path)} data-testid={`module-${m.path.split("/").pop()}`}
              className="group text-left p-5 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-200">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${m.color}15` }}>
                <m.icon className="w-5 h-5" style={{ color: m.color }} />
              </div>
              <h3 className="text-sm font-bold text-slate-800 mb-1">{m.title}</h3>
              <p className="text-xs leading-relaxed text-slate-500">{m.desc}</p>
            </button>
          ))}
        </div>

        {/* Pharma DB info */}
        <div className="p-5 rounded-xl bg-blue-50 border border-blue-200">
          <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2">
            <Brain className="w-4 h-4 text-blue-500" /> Base de données pharmaceutique
          </h3>
          <p className="text-xs text-slate-600">
            Notre base contient les médicaments les plus courants en Côte d'Ivoire avec leurs interactions,
            contre-indications et posologies. Enrichie par l'IA Claude pour des analyses cliniques approfondies.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {["Antalgiques", "Antibiotiques", "Antipaludéens", "Antihypertenseurs", "Antidiabétiques", "Anticoagulants", "AINS", "Corticoïdes"].map(c => (
              <span key={c} className="px-2.5 py-1 rounded-lg text-[10px] font-medium bg-blue-100 text-blue-700">{c}</span>
            ))}
          </div>
        </div>
      </div>
    </PharmatechLayout>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-xs font-medium text-slate-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
    </div>
  );
}

function RiskPill({ label, count, color }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
      <div className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <span className="text-xs font-bold" style={{ color }}>{count}</span>
    </div>
  );
}
